import { Button, ButtonSize } from '@atlas/components';
import classnames from 'classnames';
import {
  type FC,
  type MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import QRCode from 'react-qr-code';

import Icon from 'components/Icon/Icon';
import type { PaymentPopupDataType } from 'config/types';
import useLayout, { LayoutType } from 'utils/layout';
import { Translation } from 'utils/translations';

import styles from './PixForm.module.scss';

export type PixDepositFormProps = {
  data: PaymentPopupDataType;
};

const PixDepositForm: FC<PixDepositFormProps> = ({ data }) => {
  const layout = useLayout();
  const { redirectUrl, imageSources } = data;
  const isDesktop = layout === LayoutType.wide;
  const [isCopied, setIsCopied] = useState(false);
  const buttonLabel = useMemo(
    () => (isCopied ? 'copied' : 'copy_pix_code'),
    [isCopied],
  );

  const copyPixLink = useCallback(
    async (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();

      try {
        await navigator.clipboard.writeText(redirectUrl);
        setIsCopied(true);
      } catch (err) {
        console.error(err);
      }
    },
    [redirectUrl],
  );

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    if (isCopied) {
      timer = setTimeout(() => {
        setIsCopied(false);
      }, 10000);
    }

    return () => {
      clearTimeout(timer);
    };
  }, [isCopied]);

  return (
    <div
      className={classnames(styles.wrapper, { [styles.mobile]: !isDesktop })}
    >
      {imageSources.DEPOSIT && (
        <img
          className={classnames(styles['payment-image-deposit'], {
            [styles.mobile]: !isDesktop,
          })}
          src={imageSources.DEPOSIT}
          alt="PIX"
        />
      )}
      <h3 className={styles.title}>
        <Translation message="pix_deposit_modal_title" />
      </h3>
      {isDesktop && (
        <p className={styles.description}>
          <span className={styles.bold}>
            <Translation message="pix_deposit_modal_option_one" />
          </span>
          <Translation message="pix_deposit_modal_option_one_description" />
        </p>
      )}
      {isDesktop && redirectUrl && (
        <QRCode
          value={redirectUrl}
          className={styles.code}
          size={213}
          level={'L'}
          style={{ background: 'white', padding: '20px' }}
        />
      )}
      <p className={styles.description}>
        {isDesktop && (
          <span className={styles.bold}>
            <Translation message="pix_deposit_modal_option_two" />
          </span>
        )}
        <Translation message="pix_deposit_modal_option_two_description" />
      </p>
      <div
        className={classnames(styles.buttons, { [styles.mobile]: !isDesktop })}
      >
        <Button
          wide
          size={ButtonSize.big}
          onClick={copyPixLink}
          icon={<Icon name="copy-success.outline" />}
          data-testid="copy"
          className={styles.button}
        >
          <Translation message={buttonLabel} />
        </Button>
      </div>
    </div>
  );
};

export default PixDepositForm;
